import React from 'react';
import { SimpleGrid } from '@chakra-ui/react';
import { Button } from './button';
import { useA11yAnnounce } from './AccessibilityAnnounce';
import { useTranslation } from 'react-i18next';

export interface DrawerFormFooterProps {
  /** Cancel/Close button handler */
  onCancel: () => void;
  /** Cancel button label - defaults to 'Cancel' */
  cancelLabel?: string;
  /** Save/Submit button handler - if omitted, no save button is shown */
  onSave?: () => void;
  /** Save button label - defaults to 'Save' */
  saveLabel?: string;
  /** Whether save button is disabled */
  saveDisabled?: boolean;
  /** Whether save action is loading */
  isLoading?: boolean;
  /** Form id for submit button - uses type="submit" form={formId} instead of onClick */
  formId?: string;
  /** Danger action (e.g., Delete) - renders as first button */
  dangerAction?: {
    label: string;
    onClick: () => void;
  };
  e2eTitle?: string;
}

/**
 * @useFor the sticky action row at the bottom of a `<DrawerComponent>`
 *   form — Save (primary, right-aligned) + Cancel (ghost) +
 *   optional Delete (danger, left-aligned).
 *
 *   Examples in-tree: every edit drawer in SMS / AIM / Roster.
 *
 *   Bakes in the standard button order, loading state, disabled
 *   state, and danger-action separation so callers don't have to
 *   re-implement the convention each time. For drawer footers
 *   that aren't form actions (e.g. an info-only "Close" button)
 *   use the drawer's footer slot directly with `<Button>`s.
 */
export const DrawerFormFooter = ({
  onCancel,
  cancelLabel,
  onSave,
  saveLabel,
  saveDisabled = false,
  isLoading = false,
  formId,
  dangerAction,
  e2eTitle,
}: DrawerFormFooterProps) => {
  // Accessibility announcer (ADR-057 / ADR-091). No-op without an
  // <AccessibilityAnnounceProvider> ancestor, so wiring is additive — the
  // underlying onSave/onCancel/dangerAction handlers still fire unchanged.
  // Mirrors the save/cancel/danger-label announcements SettingsDrawer baked
  // in via speakAccessibility(). NOTE: the formId submit path (type="submit",
  // no onClick) does not announce here — announcing on submit is out of scope
  // for this ticket; the onClick path is the common case.
  const announce = useA11yAnnounce();
  const { t } = useTranslation();
  const hasSave = onSave !== undefined || formId !== undefined;
  const hasDanger = dangerAction !== undefined;

  const columns = hasSave ? (hasDanger ? 3 : 2) : 1;

  // The default save label comes from the catalog, matching the
  // sibling cancel/close labels. A caller-supplied `saveLabel` is already
  // localized by the caller, so it is used verbatim.
  const resolvedSaveLabel = saveLabel || t('save');
  // Never derive the in-progress label from the save label. The previous
  // `label.replace(/e$/, '') + 'ing...'` was English-only gerund surgery and
  // mangled most real call sites even in English ("Submit" -> "Submiting...",
  // "Check In" -> "Check Ining..."), and every non-English label outright
  // ("保存" -> "保存ing..."). Only the component's own default label has a
  // known in-progress form, so only that case swaps text; a custom label is
  // left intact and the busy state is carried by `disabled` + `aria-busy`.
  const displaySaveLabel = isLoading && !saveLabel ? t('saving') : resolvedSaveLabel;

  return (
    <SimpleGrid columns={columns} gap={3} width="100%">
      {hasDanger && (
        <Button
          visual="alert"
          onClick={() => {
            dangerAction.onClick();
            announce(dangerAction.label);
          }}
          data-e2e={e2eTitle ? `${e2eTitle}-danger-btn` : undefined}
        >
          {dangerAction.label}
        </Button>
      )}
      <Button
        visual="secondary"
        onClick={() => {
          onCancel();
          announce('cancel');
        }}
        data-e2e={e2eTitle ? `${e2eTitle}-cancel-btn` : undefined}
      >
        {cancelLabel || (hasSave ? t("cancel") : t("close"))}
      </Button>
      {hasSave && (
        <Button
          visual="primary"
          disabled={saveDisabled || isLoading}
          // With the gerund text gone, `aria-busy` is what tells
          // assistive tech the action is in flight — including for callers
          // that supply their own (now unmodified) saveLabel.
          aria-busy={isLoading || undefined}
          {...(formId
            ? { type: 'submit' as const, form: formId }
            : {
              onClick: () => {
                onSave?.();
                announce('save');
              },
            })}
          data-e2e={e2eTitle ? `${e2eTitle}-save-btn` : undefined}
        >
          {displaySaveLabel}
        </Button>
      )}
    </SimpleGrid>
  );
};
